import { LitElement, html, css } from 'lit';

/**
 * Enhanced XML Viewer with better syntax highlighting and UX
 * Usage: <xml-viewer .xml=${xmlString}></xml-viewer>
 */
export class XmlViewer extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      font-size: 13px;
      line-height: 1.7;
    }

    .viewer-container {
      border: 1px solid #e1e4e8;
      border-radius: 8px;
      overflow: hidden;
      background: #0d1117;
    }

    .viewer-header {
      background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
      padding: 12px 16px;
      border-bottom: 1px solid #30363d;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .viewer-title {
      color: #c9d1d9;
      font-size: 13px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .viewer-actions {
      display: flex;
      gap: 8px;
    }

    .action-btn {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: #c9d1d9;
      padding: 6px 12px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .action-btn:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .viewer {
      background: #0d1117;
      padding: 16px;
      overflow: auto;
      max-height: 600px;
      color: #c9d1d9;
    }

    details {
      padding-left: 20px;
      border-left: 2px solid #21262d;
      margin: 0;
    }

    details[open] {
      border-left-color: #58a6ff;
    }

    summary {
      list-style: none;
      cursor: pointer;
      user-select: none;
      outline: none;
      color: #8b949e;
      white-space: nowrap;
      margin: 0;
      padding: 2px 0;
      display: inline-block;
      transition: color 0.2s;
    }

    summary:hover {
      color: #c9d1d9;
    }

    summary::-webkit-details-marker {
      display: none;
    }

    details[open] > summary::before {
      content: '▼ ';
      color: #58a6ff;
      margin-right: 4px;
    }

    details:not([open]) > summary::before {
      content: '▶ ';
      color: #8b949e;
      margin-right: 4px;
    }

    .row { 
      white-space: nowrap;
      margin: 0;
      padding: 2px 0;
      display: block;
    }
    
    .tag { 
      color: #7ee787;
      font-weight: 500;
    }
    
    .attr-name { 
      color: #79c0ff;
      font-style: italic;
    }
    
    .attr-value { 
      color: #a5d6ff;
    }
    
    .text-node { 
      color: #e6edf3;
      background: rgba(110, 118, 129, 0.1);
      padding: 2px 4px;
      border-radius: 3px;
    }
    
    .comment { 
      color: #8b949e;
      font-style: italic;
    }
    
    .pi { 
      color: #ffa657;
      margin-bottom: 8px;
      display: block;
      font-weight: 500;
    }

    .expand-all-btn {
      background: rgba(88, 166, 255, 0.1);
      border-color: rgba(88, 166, 255, 0.3);
      color: #58a6ff;
    }

    .expand-all-btn:hover {
      background: rgba(88, 166, 255, 0.2);
    }
  `;

  static properties = {
    xml: { type: String }
  };

  constructor() {
    super();
    this.xml = '';
  }

  escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  parse(xml) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, 'application/xml');
      const err = doc.querySelector('parsererror');
      if (err) return { error: err.textContent || 'XML parse error' };
      return { doc };
    } catch (e) {
      return { error: e.message };
    }
  }

  toTree(node) {
    if (node.nodeType !== 1) return null;
    const el = node;
    const attrs = el.attributes ? Array.from(el.attributes).map(a => ({ name: a.name, value: a.value })) : [];
    const children = [];
    el.childNodes.forEach((c) => {
      if (c.nodeType === 1) {
        const t = this.toTree(c);
        if (t) children.push(t);
      } else if (c.nodeType === 3) {
        const txt = (c.textContent || '').trim();
        if (txt) children.push({ type: 'text', text: txt });
      } else if (c.nodeType === 8) {
        const txt = (c.textContent || '').trim();
        if (txt) children.push({ type: 'comment', text: txt });
      }
    });
    return { type: 'element', name: el.nodeName, attrs, children };
  }

  renderAttrs(attrs) {
    if (!attrs || !attrs.length) return '';
    return html`${attrs.map(a => html` <span class="attr-name">${a.name}</span>="<span class="attr-value">${this.escape(a.value)}</span>"`)}`;
  }

  renderNode(node) {
    if (!node) return html``;
    if (node.type === 'text') {
      return html`<span class="text-node">${this.escape(node.text)}</span>`;
    }
    if (node.type === 'comment') {
      return html`<div class="row comment">&lt;!-- ${this.escape(node.text)} --&gt;</div>`;
    }

    const hasChildren = (node.children || []).some(c => c.type === 'element');
    const hasTextOnly = (node.children || []).length > 0 && (node.children || []).every(c => c.type !== 'element');

    if (!hasChildren && hasTextOnly) {
      const textContent = node.children.map(c => c.type === 'text' ? c.text : '').join(' ').trim();
      return html`
        <div class="row">
          <span class="tag">&lt;${node.name}</span>${this.renderAttrs(node.attrs)}<span class="tag">&gt;</span><span class="text-node">${this.escape(textContent)}</span><span class="tag">&lt;/${node.name}&gt;</span>
        </div>
      `;
    }

    return html`
      <details>
        <summary>
          <span class="tag">&lt;${node.name}</span>${this.renderAttrs(node.attrs)}<span class="tag">&gt;</span>
        </summary>
        ${(node.children || []).map(child => this.renderNode(child))}
        <div class="row"><span class="tag">&lt;/${node.name}&gt;</span></div>
      </details>
    `;
  }

  expandAll() {
    const details = this.shadowRoot.querySelectorAll('details');
    details.forEach(d => d.open = true);
  }

  collapseAll() {
    const details = this.shadowRoot.querySelectorAll('details');
    details.forEach(d => d.open = false);
  }

  copyToClipboard() {
    if (navigator.clipboard && this.xml) {
      navigator.clipboard.writeText(this.xml).then(() => {
        // Could add a toast notification here
        console.log('XML copied to clipboard');
      });
    }
  }

  render() {
    if (!this.xml || typeof this.xml !== 'string') return html``;
    const declMatch = this.xml.match(/^<\?xml[^>]*>/i);
    const parsed = this.parse(this.xml);

    if (parsed.error) {
      return html`
        <div class="viewer-container">
          <div class="viewer-header">
            <div class="viewer-title">⚠️ XML Parse Error</div>
          </div>
          <div class="viewer">
            <div class="row" style="color:#f85149;">${parsed.error}</div>
          </div>
        </div>
      `;
    }

    const root = this.toTree(parsed.doc.documentElement);

    return html`
      <div class="viewer-container">
        <div class="viewer-header">
          <div class="viewer-title">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm1 3v6h2V5H5zm4 0v6h2V5H9z"/>
            </svg>
            XML Preview
          </div>
        </div>
        <div class="viewer">
          ${declMatch ? html`<span class="pi">${this.escape(declMatch[0])}</span>` : ''}
          ${this.renderNode(root)}
        </div>
      </div>
    `;
  }
}

customElements.define('xml-viewer', XmlViewer);
